import SoftBox from "components/SoftBox";
import { useSlugFlow } from "hooks/useSlugFlow";
import SoftInput from "components/SoftInput";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import { useMemo, useState } from "react";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { VersionAction } from "./action";
import DeleteFlowVersionDialog from "layouts/Flow Designer/components/Titlebar/components/DeleteFlowVersionDialog";
import useFlowVersions from "hooks/useFlowVersions";
import { useResourceListener } from "hooks/useResourceListener";

export const Versions = () => {
   const { flow } = useSlugFlow();
   const [searchName, setSearchName] = useState("");
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(flow?.id);
   const { flowVersions, handleFetchFlowVersions } = useFlowVersions(flow.id);
   useResourceListener("flowsVersionUpdate", handleFetchFlowVersions);

   const handleFilterVersions = (version) => {
      return version?.name?.toLowerCase()?.includes(searchName.toLowerCase());
   };

   const sortVersions = (a, b) => b.version.localeCompare(a.version, undefined, { numeric: true });

   const tableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "Version",
               accessor: "version",
               disableSortBy: false,
               Cell: ({ value }) => (
                  <SoftBox sx={{ marginLeft: "34px" }}>
                     <SoftTypography fontSize="12px" fontWeight="bold">
                        {value}
                     </SoftTypography>
                  </SoftBox>
               )
            },
            {
               Header: "Status",
               accessor: "status",
               disableSortBy: false,
               Cell: ({ value }) => <StatusBadge status={value} />
            },
            {
               Header: "Created",
               accessor: "created",
               disableSortBy: false,
               disableGlobalFilter: true,
               Cell: ({ value }) => <SoftTypography fontSize="12px">{value}</SoftTypography>
            },
            {
               Header: "Last updated",
               accessor: "last_updated",
               disableSortBy: false,
               Cell: ({ value }) => <SoftTypography fontSize="12px">{value}</SoftTypography>
            },
            {
               Header: "Actions",
               accessor: "actions",
               disableSortBy: true,
               disableGlobalFilter: true
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: flowVersions
            ? [
                 ...(flowVersions
                    .filter(handleFilterVersions)
                    ?.sort(sortVersions)
                    ?.map((version) => {
                       return {
                          version: version.version,
                          status: version.status,
                          created: version.created_datetime ? formatDateAndTime(getLocalDateTimeString(version.created_datetime)) : "",
                          last_updated: version.updated_datetime ? formatDateAndTime(getLocalDateTimeString(version.updated_datetime)) : "",
                          actions: <VersionAction version={version} iconOrientation="horizontal" isEditor={!isReadOnlyAccess} />
                       };
                    }) ?? [])
              ]
            : []
      };
   }, [flowVersions, searchName, isReadOnlyAccess]);

   return (
      <>
         <SoftBox>
            <SoftBox
               sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  justifyContent: "flex-start",
                  padding: "12px 0 12px 40px"
               }}
            >
               <SoftInput
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                     setSearchName(currentTarget.value);
                  }}
                  sx={{
                     maxWidth: "380px",
                     marginRight: "34px"
                  }}
               />
            </SoftBox>
            <SoftBox>
               <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons tableView={"list"} tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
            </SoftBox>
            <DeleteFlowVersionDialog />
         </SoftBox>
      </>
   );
};
